import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../../Assets/logo.png";

import ApiServices from "../../Services/ApiServices/ApiServices";

const ReferalLogin = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const toggleShowPassword = () => setShowPassword((s) => !s);
  const closeSnack = () =>
    setSnack((s) => ({ ...s, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSnack({ open: false, message: "", severity: "info" });

    try {
      const res = await ApiServices.post(
        "/referral-sources/login/",
        {
          email: email.trim(),
          password,
        }
      );

      const data = res?.data || {};

      if (!data?.success) {
        throw new Error(data?.message || "Login failed");
      }

      // Store tokens
      if (data?.access) sessionStorage.setItem("authToken", data.access);
      if (data?.refresh)
        sessionStorage.setItem("refreshToken", data.refresh);

      // Store user info
      sessionStorage.setItem("userType", "referral_source");
      sessionStorage.setItem(
        "user",
        JSON.stringify(data?.user || {})
      );

      setSnack({
        open: true,
        message: "Login successful",
        severity: "success",
      });

      // Referral dashboard route
      navigate("/referral/add-referral");
    } catch (err) {
      let message = "Login failed. Please check credentials.";
      if (err?.response?.data?.message)
        message = err.response.data.message;
      else if (err?.message) message = err.message;

      setSnack({ open: true, message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={rootStyles}>
      <style>{`
        .login-card {
          width: 420px;
          border-radius: 12px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(9, 76, 159, 0.12);
        }

        .login-card-content {
          padding: 32px 28px 36px;
          display: flex;
          flex-direction: column;
        }

        .logo-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .login-logo {
          width: 115px;
          height: 100px;
          object-fit: contain;
          border-radius: 8px;
        }

        .login-title {
          text-align: center;
          color: #094c9f;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .login-btn {
          margin-top: 14px;
          background-color: #094c9f;
          text-transform: none;
          font-weight: 600;
          padding: 10px;
        }

        .login-btn:hover {
          background-color: #083b82;
        }

        @media (max-width: 480px) {
          .login-card {
            width: 92%;
          }
        }
      `}</style>

      <Card className="login-card">
        <CardContent className="login-card-content">
          {/* Centered Logo */}
          <Box className="logo-wrap">
            <img src={logo} alt="logo" className="login-logo" />
          </Box>

          <Typography variant="h5" className="login-title">
            Referral Sign in
          </Typography>

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="login-btn"
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} /> : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={closeSnack}
      >
        <Alert onClose={closeSnack} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferalLogin;

/* container styles */
const rootStyles = {
  minHeight: "85vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
  padding: "24px",
};
